import { useEffect, useMemo, useState } from "react";
import { api, API, fmtBRL, fmtBRDateTime, todayBR } from "@/lib/api";
import { toast } from "sonner";
import NfeFixCustomerModal from "@/components/fiscal/NfeFixCustomerModal";
import {
  ArrowClockwise,
  ArrowsCounterClockwise,
  DownloadSimple,
  EnvelopeSimple,
  XCircle,
  PencilSimpleLine,
  MagnifyingGlass,
  FileXls,
} from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "processando", label: "Processando" },
  { value: "autorizada", label: "Autorizada" },
  { value: "aguardando_processamento", label: "Aguardando" },
  { value: "em_digitacao", label: "Em digitação" },
  { value: "rejeitada", label: "Rejeitada" },
  { value: "cancelada", label: "Cancelada" },
  { value: "denegada", label: "Denegada" },
];

const STATUS_BADGE = {
  autorizada: "bg-green-600 text-white",
  rejeitada: "bg-red-600 text-white",
  cancelada: "bg-zinc-800 text-white",
  aguardando_processamento: "bg-yellow-500 text-black",
  em_digitacao: "bg-blue-500 text-white",
  processando: "bg-orange-500 text-white animate-pulse",
  denegada: "bg-red-800 text-white",
};

const TRANSIENT_STATUSES = ["processando", "em_digitacao", "aguardando_processamento"];

const todayLocal = () => todayBR();
const monthStart = () => {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit" }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  return `${y}-${m}-01`;
};

export default function FiscalHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    period_start: monthStart(),
    period_end: todayLocal(),
    status: "",
    customer: "",
    number: "",
  });
  const [modal, setModal] = useState(null); // { type, nfe }
  const [fixModal, setFixModal] = useState(null); // { nfe } — modal de "corrigir dados do cliente e reprocessar"
  const [text, setText] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/fiscal/nfe", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Falha ao carregar histórico: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  // Executa `load` uma vez na montagem. `filters` NÃO deve entrar nas deps aqui:
  // o usuário aplica os filtros clicando no botão "Filtrar" (line 259). Adicionar
  // filters causaria fetch a cada tecla digitada.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Polling automático: enquanto houver NF-e em estado transitório, consulta
  // o /fiscal/nfe/{id} (endpoint leve, sem PIN) a cada 4s pra atualizar o
  // status. Para o polling quando todos atingem estado terminal.
  useEffect(() => {
    const transient = items.filter((n) => TRANSIENT_STATUSES.includes(n.status));
    if (transient.length === 0) return undefined;
    const timer = setInterval(async () => {
      try {
        const results = await Promise.all(
          transient.map((n) => api.get(`/fiscal/nfe/${n.id}`).then((r) => r.data).catch(() => null))
        );
        setItems((prev) => prev.map((n) => {
          const updated = results.find((r) => r && r.id === n.id);
          return updated || n;
        }));
      } catch (err) {
        console.warn("Polling de status NF-e falhou:", err?.message);
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [items]);

  const refresh = async (nfe) => {
    try {
      await api.post(`/fiscal/nfe/${nfe.id}/refresh`);
      toast.success("Status atualizado");
      await load();
    } catch (e) {
      toast.error("Falha: " + (e?.response?.data?.detail || e.message));
    }
  };

  const retry = async (nfe) => {
    // Se a rejeição foi por dados do cliente, abre o modal de correção antes
    if (nfe.retry_kind === "fix_customer") {
      setFixModal({ nfe });
      return;
    }
    // Se foi por escopo do Bling, avisa o admin em vez de tentar
    if (nfe.retry_kind === "reconnect_bling") {
      toast.error(
        "Reconecte o Bling: Configurações → Bling → Desconectar. No app Bling, habilite o escopo 'Notas Fiscais Eletrônicas' e reconecte.",
        { duration: 12000 }
      );
      return;
    }
    if (!confirm(`Reprocessar a NF-e do pedido ${nfe.customer || "—"}?`)) return;
    try {
      await api.post(`/fiscal/nfe/${nfe.id}/retry`);
      toast.success("Reprocessamento iniciado — acompanhe o status");
      await load();
    } catch (e) {
      toast.error("Falha: " + (e?.response?.data?.detail || e.message));
    }
  };

  const downloadPdf = async (nfe) => {
    try {
      const res = await api.get(`/fiscal/nfe/${nfe.id}/danfe.pdf`, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `danfe_${nfe.number || nfe.id}.pdf`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error("DANFE indisponível: " + (e?.response?.data?.detail || "verifique se a NF-e está autorizada"));
    }
  };

  const openModal = (type, nfe) => {
    setModal({ type, nfe });
    setText("");
    setEmailTo(nfe.customer_email || "");
  };

  const submitModal = async () => {
    if (!modal) return;
    setBusy(true);
    try {
      const { type, nfe } = modal;
      if (type === "cancel") {
        if (text.trim().length < 15) throw new Error("A justificativa precisa de pelo menos 15 caracteres");
        await api.post(`/fiscal/nfe/${nfe.id}/cancel`, { justification: text });
        toast.success("NF-e cancelada");
      } else if (type === "cce") {
        if (text.trim().length < 15) throw new Error("O texto da CCe precisa de pelo menos 15 caracteres");
        await api.post(`/fiscal/nfe/${nfe.id}/cce`, { correction: text });
        toast.success("Carta de correção enviada");
      } else if (type === "email") {
        if (!emailTo || !emailTo.includes("@")) throw new Error("Informe um e-mail válido");
        await api.post(`/fiscal/nfe/${nfe.id}/send-email`, { email: emailTo, message: text || undefined });
        toast.success(`E-mail enviado para ${emailTo}`);
      }
      setModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Falha na operação");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    if (items.length === 0) { toast.error("Nada para exportar"); return; }
    const header = ["Número", "Série", "Cliente", "Documento", "Status", "Valor", "Chave", "Emitida em"];
    const rows = items.map((n) => [
      n.number || "",
      n.series || "",
      (n.customer || "").replace(/"/g, "'"),
      n.customer_document || "",
      n.status || "",
      String(n.total_value || 0).replace(".", ","),
      n.access_key || "",
      fmtBRDateTime(n.created_at),
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${c}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `nfes_${filters.period_start}_${filters.period_end}.csv`;
    a.click();
    URL.revokeObjectURL(href);
  };

  const totals = useMemo(() => {
    const auth = items.filter((n) => n.status === "autorizada");
    const totalAuth = auth.reduce((s, n) => s + Number(n.total_value || 0), 0);
    return { total: items.length, autorizadas: auth.length, valor: totalAuth };
  }, [items]);

  const scopeIssue = useMemo(
    () => items.some((n) => n.status === "rejeitada" && n.error_type === "scope"),
    [items],
  );

  return (
    <div className="space-y-4">
      {scopeIssue && (
        <div className="border border-red-600 bg-red-50 p-4 flex items-start gap-3" data-testid="fiscal-hist-scope-banner">
          <span className="text-xl">⚠️</span>
          <div className="flex-1 text-sm">
            <div className="font-bold text-red-800">Bling sem permissão para emitir NF-e</div>
            <div className="text-red-700 mt-1">
              Suas últimas emissões falharam com <b>"insufficient_scope"</b>. Para resolver:
            </div>
            <ol className="text-red-700 mt-2 pl-5 list-decimal space-y-1">
              <li>Vá em <a href="https://developer.bling.com.br/aplicativos" target="_blank" rel="noreferrer" className="underline">developer.bling.com.br/aplicativos</a> → seu app → aba <b>Escopos</b></li>
              <li>Habilite <b>"Notas Fiscais Eletrônicas"</b> (leitura + escrita)</li>
              <li>No PopCopos: Configurações → Bling → <b>Desconectar</b> e depois <b>Conectar com Bling</b> de novo</li>
              <li>Volte aqui e clique em <b>Reprocessar</b> nas NF-es rejeitadas</li>
            </ol>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="border border-black bg-white">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-0">
          <FilterCell label="De">
            <input type="date" value={filters.period_start} onChange={(e) => setFilters({ ...filters, period_start: e.target.value })}
              className="w-full outline-none text-sm font-mono" data-testid="fiscal-hist-date-start" />
          </FilterCell>
          <FilterCell label="Até">
            <input type="date" value={filters.period_end} onChange={(e) => setFilters({ ...filters, period_end: e.target.value })}
              className="w-full outline-none text-sm font-mono" data-testid="fiscal-hist-date-end" />
          </FilterCell>
          <FilterCell label="Status">
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full outline-none text-sm bg-transparent" data-testid="fiscal-hist-status">
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </FilterCell>
          <FilterCell label="Cliente">
            <input value={filters.customer} onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
              placeholder="Buscar…" className="w-full outline-none text-sm" data-testid="fiscal-hist-customer" />
          </FilterCell>
          <FilterCell label="Nº / Chave">
            <input value={filters.number} onChange={(e) => setFilters({ ...filters, number: e.target.value })}
              placeholder="Nº ou chave" className="w-full outline-none text-sm font-mono" data-testid="fiscal-hist-number" />
          </FilterCell>
          <div className="border-l border-black flex">
            <button onClick={load} className="flex-1 bg-black text-white text-sm font-bold flex items-center justify-center gap-2 px-4" data-testid="fiscal-hist-apply">
              <MagnifyingGlass size={16} weight="bold" /> Filtrar
            </button>
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Total notas" value={totals.total} testid="fiscal-hist-total-count" />
        <SummaryCard label="Autorizadas" value={totals.autorizadas} testid="fiscal-hist-auth-count" />
        <SummaryCard label="Valor autorizado" value={fmtBRL(totals.valor)} mono testid="fiscal-hist-auth-value" />
      </div>

      {/* Toolbar */}
      <div className="flex justify-end gap-2">
        <button onClick={exportCsv} className="px-3 py-1.5 border border-black text-sm hover:bg-zinc-100 flex items-center gap-2" data-testid="fiscal-hist-export">
          <FileXls size={16} weight="bold" /> Exportar CSV
        </button>
      </div>

      {/* Tabela */}
      <div className="border border-black bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-100 border-b border-black">
            <tr>
              <th className="p-3 text-left">Nº</th>
              <th className="p-3 text-left">Cliente</th>
              <th className="p-3 text-left">Documento</th>
              <th className="p-3 text-center">Status</th>
              <th className="p-3 text-right">Valor</th>
              <th className="p-3 text-left">Emitida em</th>
              <th className="p-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-zinc-400">Carregando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-zinc-400">Sem notas no filtro atual</td></tr>
            )}
            {!loading && items.map((n) => (
              <tr key={n.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`fiscal-hist-row-${n.id}`}>
                <td className="p-3 font-mono">{n.number || "—"} <span className="text-zinc-400">/{n.series || "1"}</span></td>
                <td className="p-3">{n.customer || "—"}</td>
                <td className="p-3 font-mono text-xs">{n.customer_document || "—"}</td>
                <td className="p-3 text-center">
                  <span className={`text-[10px] font-mono px-2 py-0.5 uppercase ${STATUS_BADGE[n.status] || "bg-zinc-200"}`}>
                    {(n.status || "").replace(/_/g, " ")}
                  </span>
                </td>
                <td className="p-3 text-right font-mono">{fmtBRL(n.total_value)}</td>
                <td className="p-3 text-xs font-mono text-zinc-600">{fmtBRDateTime(n.created_at)}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <IconBtn title="Atualizar status" onClick={() => refresh(n)} testid={`fiscal-refresh-${n.id}`}><ArrowClockwise size={14} weight="bold" /></IconBtn>
                    {["rejeitada", "processando"].includes(n.status) && (
                      <IconBtn title="Reprocessar NF-e" onClick={() => retry(n)} testid={`fiscal-retry-${n.id}`}><ArrowsCounterClockwise size={14} weight="bold" /></IconBtn>
                    )}
                    <IconBtn title="Baixar DANFE" onClick={() => downloadPdf(n)} disabled={n.status !== "autorizada"} testid={`fiscal-pdf-${n.id}`}><DownloadSimple size={14} weight="bold" /></IconBtn>
                    <IconBtn title="Enviar por e-mail" onClick={() => openModal("email", n)} disabled={n.status !== "autorizada"} testid={`fiscal-email-${n.id}`}><EnvelopeSimple size={14} weight="bold" /></IconBtn>
                    <IconBtn title="Carta de correção" onClick={() => openModal("cce", n)} disabled={n.status !== "autorizada"} testid={`fiscal-cce-${n.id}`}><PencilSimpleLine size={14} weight="bold" /></IconBtn>
                    <IconBtn title="Cancelar NF-e" onClick={() => openModal("cancel", n)} disabled={n.status !== "autorizada"} testid={`fiscal-cancel-${n.id}`} danger><XCircle size={14} weight="bold" /></IconBtn>
                  </div>
                  {n.rejection_reason && (
                    <div className="text-[10px] text-red-600 mt-1 max-w-xs text-right">{n.rejection_reason}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal ações */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setModal(null)}>
          <div className="bg-white border border-black w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid={`fiscal-modal-${modal.type}`}>
            <div className="border-b border-black px-5 py-4">
              <div className="overline text-zinc-500 text-xs">NF-e {modal.nfe.number || "—"} · {modal.nfe.customer}</div>
              <div className="font-head font-black text-lg">
                {modal.type === "cancel" && "Cancelar NF-e"}
                {modal.type === "cce" && "Carta de correção (CCe)"}
                {modal.type === "email" && "Enviar DANFE por e-mail"}
              </div>
            </div>
            <div className="p-5 space-y-3">
              {modal.type === "email" && (
                <div>
                  <label className="block text-xs uppercase tracking-wide text-zinc-600 mb-1">Destinatário</label>
                  <input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} type="email"
                    className="border border-black px-3 py-2 w-full text-sm" data-testid="fiscal-modal-email-to" />
                </div>
              )}
              <div>
                <label className="block text-xs uppercase tracking-wide text-zinc-600 mb-1">
                  {modal.type === "cancel" && "Justificativa (mín. 15 caracteres)"}
                  {modal.type === "cce" && "Texto da correção (mín. 15 caracteres)"}
                  {modal.type === "email" && "Mensagem (opcional)"}
                </label>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
                  className="border border-black px-3 py-2 w-full text-sm"
                  placeholder={modal.type === "email" ? "Segue em anexo o DANFE…" : "Descreva com clareza…"}
                  data-testid="fiscal-modal-text" />
                {(modal.type === "cancel" || modal.type === "cce") && (
                  <div className="text-[11px] text-zinc-500 mt-1 font-mono">{text.trim().length}/15</div>
                )}
              </div>
            </div>
            <div className="border-t border-black px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setModal(null)} disabled={busy} className="px-4 py-2 text-sm border border-black hover:bg-zinc-100" data-testid="fiscal-modal-cancel">Cancelar</button>
              <button onClick={submitModal} disabled={busy}
                className={`px-4 py-2 text-sm font-bold text-white disabled:opacity-50 ${modal.type === "cancel" ? "bg-red-700 hover:bg-red-800" : "bg-black hover:bg-zinc-800"}`}
                data-testid="fiscal-modal-confirm"
              >
                {busy ? "Enviando ao Bling…" : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {fixModal && (
        <NfeFixCustomerModal
          nfe={fixModal.nfe}
          onClose={() => setFixModal(null)}
          onRetried={load}
        />
      )}
    </div>
  );
}

function FilterCell({ label, children }) {
  return (
    <div className="p-3 border-r border-b md:border-b-0 border-black last:border-r-0">
      <div className="overline text-[10px] text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function SummaryCard({ label, value, mono, testid }) {
  return (
    <div className="border border-black bg-white p-4" data-testid={testid}>
      <div className="overline text-xs text-zinc-500">{label}</div>
      <div className={`text-2xl font-black mt-1 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function IconBtn({ children, title, onClick, disabled, danger, testid }) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`p-1.5 border border-black disabled:opacity-30 disabled:cursor-not-allowed ${
        danger ? "hover:bg-red-600 hover:text-white hover:border-red-600" : "hover:bg-black hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}
